// ─── Region & Postcode ────────────────────────────────────────────────

export interface PostcodeRange {
  zone: string          // 'Continental', 'Madeira', 'Açores'
  from: string          // '1000' — inclusive lower bound
  to: string            // '9999' — inclusive upper bound
  excludedSellers?: string[]  // sellers to hide for users in this postcode zone
}

export interface RegionServiceConfig {
  name: string
  active: boolean
  isDefault: boolean
  deliveryDays: number
  maxWeight?: number     // grams
  maxVolume?: number     // cm³

  // V3.1: Seller exclusions
  excludedSellers?: string[]

  // Rate calculation
  calcMethod: CalcMethod
  fixedCost?: number
  perUnitCost?: number
  costPerGram?: number
  costPerCm3?: number
  useWeight?: boolean
  ranges?: Array<{ min: number; max: number; cost: number }>

  // V3.2: FNAC logistic category rates
  categoryRates?: Record<string, { fixedCost: number; perUnitCost: number }>
}

export interface Region {
  id: string
  name: string
  countries: string[]
  postcodeRanges: PostcodeRange[]
  minOrderValue: number
  enabled: boolean
  services: Record<string, RegionServiceConfig>
  createdAt: Date
  updatedAt: Date
}

// ─── Calculation ──────────────────────────────────────────────────────

export type CalcMethod =
  | 'fixed_per_unit'
  | 'fixed_per_range'
  | 'fixed_variable_weight'
  | 'fixed_variable_volume'
  | 'fnac_logistic_category'
  | 'fixed_variable_range'

export type LogisticCategory = 'P' | 'M' | 'G' | 'A'

export interface ResolvedService extends RegionServiceConfig {
  serviceId: string
  resolvedCost: number  // cost after applying product rate override (if any)
}

// ─── Product Shipping Override ────────────────────────────────────────

export interface ProductServiceOverride {
  calcMethod?: CalcMethod
  fixedCost?: number
  perUnitCost?: number
  costPerGram?: number
  costPerCm3?: number
  useWeight?: boolean
  ranges?: Array<{ min: number; max: number; cost: number }>
  categoryRates?: Record<string, { fixedCost: number; perUnitCost: number }>
}

export interface ProductRegionOverride {
  services: Record<string, ProductServiceOverride>
}

export interface ProductShippingPolicies {
  [regionId: string]: ProductRegionOverride
}

// ─── Cart Metadata ────────────────────────────────────────────────────

export interface CartMeta {
  shippingCountry: string              // default 'PT'
  shippingPostcode?: string            // '1000-001'
  selectedDeliveryAddressId?: string
  selectedServices?: Record<string, string>  // regionId → serviceId
}

// ─── Transport Line ───────────────────────────────────────────────────

export interface TransportLine {
  name: string
  points: number
  amtPoints: number
  qty: number
  isTransport: true
  nonBillable: boolean
  vatRate: number

  // V3: Shipping-specific
  regionId?: string
  regionName?: string
  serviceId?: string
  serviceName?: string
  deliveryDays?: number
  shippingCostEuros?: number
  isPendingAddress?: boolean
  unavailable?: boolean
}
